/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { IconX, IconMapPin, IconUser, IconPackage } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import { OrderFormData, PAYMENT_TYPE_OPTIONS } from "../../../types/types";
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from "react-hook-form";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";
import {
  useDistrictFetchUpdateData,
  useFetchCountriesAllData,
  useFetchCountryCitiesUpdateAllData,
} from "../../../apis/locationApis";
import { T, useLanguage } from "../../../context/LanguageContext";

type Props = {
  open: boolean;
  onClose: () => void;
  order: any;
  register: UseFormRegister<OrderFormData>;
  errors: FieldErrors<OrderFormData>;
  setValue: UseFormSetValue<OrderFormData>;
  watch: any;
  clearErrors: UseFormClearErrors<OrderFormData>;
  handleSubmit: React.MouseEventHandler<HTMLButtonElement>;
  isPending: boolean;
  inPlace?: boolean;
  height?: number;
};

const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
  <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3">
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

const EditOrderModal = ({
  open,
  onClose,
  order,
  register,
  errors,
  setValue,
  watch,
  clearErrors,
  handleSubmit,
  isPending,
  inPlace = false,
  height,
}: Props) => {
  const { t } = useLanguage();
  const [editMode, setEditMode] = useState(false);

  // API data
  const { data: countries = [] } = useFetchCountriesAllData();
  const { data: senderCities = [], refetch: fetchSenderCities } =
    useFetchCountryCitiesUpdateAllData(watch("sender.country"));
  const { data: senderDistricts = [], refetch: fetchSenderDistricts } =
    useDistrictFetchUpdateData(watch("sender.city"));
  const { data: receiverCities = [], refetch: fetchReceiverCities } =
    useFetchCountryCitiesUpdateAllData(watch("receiver.country"));
  const { data: receiverDistricts = [], refetch: fetchReceiverDistricts } =
    useDistrictFetchUpdateData(watch("receiver.city"));

  useEffect(() => {
    if (order?.receiverCountry) {
      fetchReceiverCities();
    }
  }, [order?.receiverCountry]);

  useEffect(() => {
    if (order?.receiverCity) {
      fetchReceiverDistricts();
    }
  }, [order?.receiverCity]);

  useEffect(() => {
    if (order?.senderCountry) {
      fetchSenderCities();
    }
  }, [order?.senderCountry]);

  useEffect(() => {
    if (order?.senderCity) {
      fetchSenderDistricts();
    }
  }, [order?.senderCity]);

  useEffect(() => {
    if (order) {
      const cleanAddressVal = (val: any) => {
        if (val === "undefined" || val === null || val === undefined) return "";
        return String(val).trim();
      };
      const senderAddr = cleanAddressVal(
        order?.senderNationalAddress ||
          order?.senderShortAddress ||
          order?.shortAddress,
      );
      const receiverAddr = cleanAddressVal(
        order?.receiverNationalAddress ||
          order?.receiverShortAddress ||
          order?.shortAddress,
      );

      setValue("sender.name", order?.senderName);
      setValue("sender.mobile", order?.senderMobile);
      setValue("sender.email", order?.senderEmail);
      setValue("sender.country", order?.senderCountry);
      setValue("sender.city", order?.senderCity);
      setValue("sender.district", order?.senderDistrict);
      setValue("sender.zipCode", order?.senderZipCode);
      setValue("sender.latitude", order?.senderLatitude);
      setValue("sender.longitude", order?.senderLongitude);
      setValue("sender.address", order?.senderAddress);
      setValue("sender.nationalAddress", senderAddr);
      setValue("sender.shortAddress", senderAddr);
      setValue("receiver.name", order?.receiverName);
      setValue("receiver.mobile", order?.receiverMobile);
      setValue("receiver.email", order?.receiverEmail);
      setValue("receiver.country", order?.receiverCountry);
      setValue("receiver.city", order?.receiverCity);
      setValue("receiver.district", order?.receiverDistrict);
      setValue("receiver.zipCode", order?.receiverZipCode);
      setValue("receiver.latitude", order?.receiverLatitude);
      setValue("receiver.longitude", order?.receiverLongitude);
      setValue("receiver.address", order?.receiverAddress);
      setValue("receiver.nationalAddress", receiverAddr);
      setValue("receiver.shortAddress", receiverAddr);
      setValue("shipment.paymentType", order?.paymentType);
      setValue("shipment.codAmount", order?.codAmount);
      setValue("shipment.numberOfPieces", order?.noOfPieces);
      setValue("shipment.totalWeight", order?.totalWeight);
    }
  }, [order]);

  if (!open) return null;

  const formContent = (
    <div className="space-y-2">
      {/* RECEIVER INFORMATION SECTION */}
      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <SectionHeader icon={IconUser} title={t("Receiver Details")} />
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

        <div className="grid grid-cols-2 gap-3">
          <NewinputField
            className="col-span-2"
            name="receiver.name"
            type="text"
            title={t("Name")}
            register={register}
            error={errors.receiver?.name}
            validation={{ required: t("Receiver name is required") }}
          />
          <NewinputField
            className="w-full"
            name="receiver.mobile"
            type="number"
            title={t("Mobile")}
            register={register}
            error={errors.receiver?.mobile}
            validation={{ required: t("Receiver mobile is required") }}
          />

          <NewinputField
            className="w-full"
            name="receiver.email"
            type="text"
            title={t("Email")}
            register={register}
            error={errors.receiver?.email}
            validation={{ required: t("Receiver email is required") }}
          />
          <NewSelectInput
            className="w-full"
            name="receiver.country"
            label={t("Country")}
            placeholder={t("Select Country")}
            selectOptions={countries.map((country: any) => ({
              id: country.id,
              value: country.id,
              text: country.name,
            }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: t("Receiver country is required") }}
            disabled={!countries.length}
            value={watch("receiver.country")}
            setValue={(val: string) => {
              const country = countries.find((c: any) => c.id === val);
              setValue("receiver.country", val);
              setValue("receiver.countryName", country?.name || "");
              setValue("receiver.city", "");
              setValue("receiver.cityName", "");
              setValue("receiver.district", "");
              setValue("receiver.districtName", "");
            }}
          />

          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              {t("Short Add")}<span className="text-red-600">*</span>
            </label>
            <input
              {...register("receiver.nationalAddress", {
                required: t("Receiver national address is required"),
              })}
              className="w-full rounded-lg border resize-none border-[#D8D8D8] px-3 py-[10px] outline-none text-xs text-[#1E1E1E] focus:border-[#FBD037]"
            />
            {errors.receiver?.nationalAddress && (
              <span className="text-red-500 text-xs block mt-0.5">
                {errors.receiver?.nationalAddress?.message}
              </span>
            )}
          </div>

          <NewSelectInput
            className="w-full"
            name="receiver.city"
            label={t("City")}
            placeholder={t("Select City")}
            selectOptions={receiverCities.map((city: any) => ({
              id: city.id,
              value: city.id,
              text: city.name,
            }))}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: t("Receiver city is required") }}
            disabled={!receiverCities.length}
            value={watch("receiver.city")}
            setValue={(val: string) => {
              const city = receiverCities?.find((c: any) => c.id === val);
              setValue("receiver.city", val);
              setValue("receiver.cityName", city?.name || "");
              setValue("receiver.district", "");
              setValue("receiver.districtName", "");
            }}
          />
          <NewSelectInput
            className="w-full"
            name="receiver.district"
            label={t("District")}
            placeholder={t("Select District")}
            selectOptions={receiverDistricts.map((district: any) => ({
              id: district.id,
              value: district.id,
              text: district.name,
            }))}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: t("Receiver district is required") }}
            disabled={!receiverDistricts.length}
            value={watch("receiver.district")}
            setValue={(val: string) => {
              const district = receiverDistricts?.find(
                (d: any) => d.id === val,
              );
              setValue("receiver.district", val);
              setValue("receiver.districtName", district?.name || "");
            }}
          />
          <NewinputField
            className="w-full"
            name="receiver.zipCode"
            type="text"
            title={t("Zip Code")}
            register={register}
            error={errors.receiver?.zipCode}
            validation={{ required: t("Receiver zip code is required") }}
          />
          <NewinputField
            className="w-full"
            name="receiver.latitude"
            type="number"
            title={t("Latitude")}
            step="any"
            inputMode="decimal"
            register={register}
            error={errors.receiver?.latitude}
            validation={{ required: t("Receiver latitude is required") }}
          />

          <NewinputField
            className="w-full"
            name="receiver.longitude"
            type="number"
            title={t("Longitude")}
            step="any"
            inputMode="decimal"
            register={register}
            error={errors.receiver?.longitude}
            validation={{ required: t("Receiver longitude is required") }}
          />
          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              {t("Address")}<span className="text-red-600">*</span>
            </label>
            <input
              {...register("receiver.address", {
                required: t("Receiver address is required"),
              })}
              className="w-full rounded-lg border resize-none border-[#D8D8D8] px-3 py-[10px] outline-none text-xs text-[#1E1E1E] focus:border-[#FBD037]"
            />
            {errors.receiver?.address && (
              <span className="text-red-500 text-xs block mt-0.5">
                {errors.receiver?.address?.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* SENDER INFORMATION SECTION */}
      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <SectionHeader icon={IconUser} title={t("Sender Details")} />
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

        <div className="grid grid-cols-2 gap-3">
          <NewinputField
            className="col-span-2"
            name="sender.name"
            type="text"
            title={t("Name")}
            register={register}
            error={errors.sender?.name}
            validation={{ required: t("Sender name is required") }}
          />
          <NewinputField
            className="w-full"
            name="sender.mobile"
            type="number"
            title={t("Mobile")}
            register={register}
            error={errors.sender?.mobile}
            validation={{ required: t("Sender mobile is required") }}
          />

          <NewinputField
            className="w-full"
            name="sender.email"
            type="text"
            title={t("Email")}
            register={register}
            error={errors.sender?.email}
            validation={{ required: t("Sender email is required") }}
          />

          <NewSelectInput
            className="w-full"
            name="sender.country"
            label={t("Country")}
            placeholder={t("Select Country")}
            selectOptions={countries.map((country: any) => ({
              id: country.id,
              value: country.id,
              text: country.name,
            }))}
            register={register}
            clearErrors={clearErrors}
            validation={{ required: t("Sender country is required") }}
            disabled={!countries.length}
            value={watch("sender.country")}
            setValue={(val: string) => {
              const country = countries.find((c: any) => c.id === val);
              setValue("sender.country", val);
              setValue("sender.countryName", country?.name || "");
              setValue("sender.city", "");
              setValue("sender.cityName", "");
              setValue("sender.district", "");
              setValue("sender.districtName", "");
            }}
          />
          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              {t("Short Address")}<span className="text-red-600">*</span>
            </label>
            <input
              {...register("sender.nationalAddress", {
                required: t("Sender national address is required"),
              })}
              className="w-full rounded-xl border border-[#D8D8D8] px-3 py-[10px] outline-none text-xs text-[#1E1E1E] focus:border-[#FBD037]"
            />
            {errors.sender?.nationalAddress && (
              <span className="text-red-500 text-xs block mt-0.5">
                {errors.sender?.nationalAddress?.message}
              </span>
            )}
          </div>
          <NewSelectInput
            className="w-full"
            name="sender.city"
            label={t("City")}
            placeholder={t("Select City")}
            selectOptions={senderCities.map((city: any) => ({
              id: city.id,
              value: city.id,
              text: city.name,
            }))}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: t("Sender city is required") }}
            disabled={!senderCities.length}
            value={watch("sender.city")}
            setValue={(val: string) => {
              const city = senderCities?.find((c: any) => c.id === val);
              setValue("sender.city", val);
              setValue("sender.cityName", city?.name || "");
              setValue("sender.district", "");
              setValue("sender.districtName", "");
            }}
          />
          <NewSelectInput
            className="w-full"
            name="sender.district"
            label={t("District")}
            placeholder={t("Select District")}
            selectOptions={senderDistricts.map((district: any) => ({
              id: district.id,
              value: district.id,
              text: district.name,
            }))}
            clearErrors={clearErrors}
            register={register}
            validation={{ required: t("Sender district is required") }}
            disabled={!senderDistricts.length}
            value={watch("sender.district")}
            setValue={(val: string) => {
              const district = senderDistricts?.find((d: any) => d.id === val);
              setValue("sender.district", val);
              setValue("sender.districtName", district?.name || "");
            }}
          />
          <NewinputField
            className="w-full"
            name="sender.zipCode"
            type="text"
            title={t("Zip Code")}
            register={register}
            error={errors.sender?.zipCode}
            validation={{ required: t("Sender zip code is required") }}
          />
          <NewinputField
            className="w-full"
            name="sender.latitude"
            type="number"
            title={t("Latitude")}
            step="any"
            inputMode="decimal"
            register={register}
            error={errors.sender?.latitude}
          />

          <NewinputField
            className="w-full"
            name="sender.longitude"
            type="number"
            title={t("Longitude")}
            step="any"
            inputMode="decimal"
            register={register}
            error={errors.sender?.longitude}
          />

          <div className="flex flex-col gap-1 w-full">
            <label className="text-[#1E293B] text-xs whitespace-nowrap">
              {t("Address")}<span className="text-red-600">*</span>
            </label>
            <input
              {...register("sender.address", {
                required: t("Sender address is required"),
              })}
              className="w-full rounded-xl border border-[#D8D8D8] px-3 py-[10px] outline-none text-xs text-[#1E1E1E] focus:border-[#FBD037]"
            />
            {errors.sender?.address && (
              <span className="text-red-500 text-xs block mt-0.5">
                {errors.sender?.address?.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* SHIPMENT DETAILS SECTION */}
      <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
        <SectionHeader icon={IconPackage} title={t("Shipment Details")} />
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

        <div className="grid grid-cols-2 gap-3">
          <NewSelectInput
            className="w-full"
            name="shipment.paymentType"
            label={t("Payment")}
            placeholder={t("Select Payment Type")}
            selectOptions={PAYMENT_TYPE_OPTIONS}
            clearErrors={clearErrors}
            register={register}
            value={watch("shipment.paymentType")}
            setValue={(val: string) => {
              setValue("shipment.paymentType", val);
              if (val !== "COD") {
                setValue("shipment.codAmount", "");
              }
            }}
            validation={{ required: t("Payment Type is required") }}
          />

          {watch("shipment.paymentType") === "COD" ? (
            <NewinputField
              className="w-full"
              name="shipment.codAmount"
              type="number"
              title={t("Amount")}
              register={register}
              value={watch("shipment.codAmount")}
              error={errors.shipment?.codAmount}
              validation={{ required: t("COD amount is required") }}
            />
          ) : (
            <div />
          )}
          <NewinputField
            name="shipment.numberOfPieces"
            className="w-full"
            type="number"
            title={t("Pieces")}
            register={register}
            error={errors.shipment?.numberOfPieces}
            value={watch("shipment.numberOfPieces")}
            validation={{
              required: t("No of pieces is required"),
              min: { value: 1, message: t("Must be at least 1") },
            }}
          />

          <NewinputField
            className="w-full"
            name="shipment.totalWeight"
            type="text"
            title={t("Weight (kg)")}
            register={register}
            error={errors.shipment?.totalWeight}
            value={watch("shipment.totalWeight")}
            validation={{
              required: t("Total weight is required"),
              pattern: {
                value: /^\d*\.?\d*$/,
                message: t("Must be a valid number"),
              },
            }}
          />
        </div>
      </div>
    </div>
  );

  if (inPlace) {
    return (
      <div
        // style={height ? { height: `${height}px` } : undefined}
        // className={`bg-white border border-slate-200/80 shadow-sm rounded-2xl overflow-hidden flex flex-col ${
        //   !height ? "lg:h-[calc(100vh-185px)] h-[550px]" : ""
        // }`}
        className="bg-white border border-slate-200/80 shadow-sm rounded-2xl overflow-hidden flex flex-col h-[calc(100vh-120px)]"
      >
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-white shrink-0">
          <div>
            <h3 className="text-base font-bold text-[#212121]">{t("Edit Order")}</h3>
            <p className="text-xs text-slate-500 font-medium font-mono mt-0.5">
              {t("AWB No.")} {order?.awbNumber}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              className={`border ${
                isPending
                  ? "bg-gray-800 border-gray-800 text-white cursor-not-allowed"
                  : "bg-[#FBD037] border-[#FBD037] hover:bg-yellow-400 hover:border-yellow-400 text-black"
              } flex items-center rounded-lg py-1.5 px-4 space-x-1 disabled:cursor-not-allowed cursor-pointer text-xs font-semibold transition`}
            >
              <span>{t("Update")}</span>
              {isPending && (
                <svg
                  className="animate-spin h-3.5 w-3.5 text-black"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16z"
                  />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors text-slate-700 bg-slate-50 cursor-pointer"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* SCROLLABLE FORM BODY */}
        <div className="w-full flex-1 overflow-y-auto min-h-0 p-5 scrollbar-clean">
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-end p-4">
      <div className="bg-white rounded-2xl xl:w-7/12 lg:w-7/12 w-11/12 h-[calc(100vh-2rem)] flex flex-col overflow-hidden shadow-2xl relative">
        {/* HEADER */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#d2d2d2] bg-white shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#212121]">{t("Edit Order")}</h3>
            <p className="text-xs text-slate-500 font-medium font-mono mt-0.5">
              {t("AWB No.")} {order?.awbNumber}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              className={`border ${
                isPending
                  ? "bg-gray-800 border-gray-800 text-white cursor-not-allowed"
                  : "bg-[#FBD037] border-[#FBD037] hover:bg-yellow-400 hover:border-yellow-400 text-black"
              } flex items-center rounded-lg py-1.5 px-4 space-x-1 disabled:cursor-not-allowed cursor-pointer text-xs font-semibold transition`}
            >
              <span>{t("Update")}</span>
              {isPending && (
                <svg
                  className="animate-spin h-3.5 w-3.5 text-black"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16z"
                  />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 bg-[#F2F2F7] cursor-pointer"
            >
              <IconX size={16} />
            </button>
          </div>
        </div>

        {/* SCROLLABLE FORM BODY */}
        <div className="w-full flex-1 overflow-y-auto min-h-0 p-5 scrollbar-clean">
          {formContent}
        </div>
      </div>
    </div>
  );
};

export default EditOrderModal;
